import type { Images } from '../../../generated';
import type { TypePolicies } from '../../../utils/types/TypePolicies';
import { isMediaUrlDifferentOrExpiring } from '../utils/isMediaUrlDifferentOrExpiring';

const isString = (value: unknown): value is string => typeof value === 'string';

function selectImageUrl(existingUrl: string, incomingUrl: string) {
  const isDifferentOrExpiring = isMediaUrlDifferentOrExpiring({
    current: existingUrl,
    next: incomingUrl,
  });

  if (isDifferentOrExpiring) {
    return incomingUrl;
  }

  return existingUrl;
}

export const typePolicies: TypePolicies = {
  AuthenticatedUser: {
    fields: {
      name: {
        /**
         * Sometimes (like in the case of read impressions) the user's name is not
         * present in the incoming value for unauth'd users. This merge function
         * ensures that the name is not overwritten by the incoming value if it
         * is an empty string.
         *
         * Use incoming if it is truthy (not an empty string), otherwise use existing.
         */
        merge: (existing, incoming) => incoming || existing || null,
      },
      profileImage: {
        /**
         * @note (SHU-7576): This merge policy checks cached URL for each image size.
         * If the cached value is expiring, or if the incoming URL is different,
         * then the incoming value is cached. Otherwise, the cached value is used.
         */
        merge: (existing, incoming) => {
          if (!existing) {
            return incoming;
          }

          if (!incoming) {
            return existing;
          }

          type ImageKey = keyof Images;
          return (Object.keys(existing) as ImageKey[]).reduce(
            (acc, key) => {
              if (key === '__typename') {
                return acc;
              }

              const existingVal = existing[key];
              const incomingVal = incoming[key];

              if (isString(existingVal) && isString(incomingVal)) {
                acc[key] = selectImageUrl(existingVal, incomingVal);
              } else if (incomingVal === null) {
                acc[key] = incomingVal;
              }

              return acc;
            },
            { ...existing } as Record<ImageKey, string | null | undefined>
          );
        },
      },
    },
  },
};
